/**
 * Celestia multiplayer game store — SERVER-AUTHORITATIVE (ADR-04 Option A).
 *
 * Celestia is a hidden-information game: the client can NEVER legally compute
 * results locally (it lacks the RNG seed, the deck order, and opponents'
 * hands). Therefore — unlike the Cascadia store — there is **NO optimistic
 * apply**. `submitAction` sends `{ type, actionId, params }` only; the store
 * renders the server-projected `ViewerState` delivered via:
 *
 *   - `GET /api/v2/game-platform/rooms/{roomId}/viewer-states?after=`
 *     (own rows only)
 *   - the per-user WS push `game:viewer_state` (ws-relay `broadcastToUser`)
 *
 * The store NEVER expects a raw `GameState`; the rendered type is
 * `ViewerState` (engine `@cloudnest/celestia-engine/types`). The distinct
 * type is the compile-time guard that no hand/seed reaches a component.
 *
 * Dedup: `lastAppliedSeq` — a viewer-state row/push with seq <=
 * lastAppliedSeq is ignored (WS echo / replay overlap).
 */

import type { Action, ViewerState } from "@cloudnest/celestia-engine";
import { useSyncExternalStore } from "react";
import {
  createGameApi,
  type GameApi,
  type GameApiMode,
} from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";
import { resolveCelestiaGameApiMode } from "./route-mode";

let gameApi: GameApi | null = null;
let gameApiMode: GameApiMode | null = null;

function getGameApiMode(): GameApiMode {
  return resolveCelestiaGameApiMode();
}

function getGameApi(): GameApi {
  const nextMode = getGameApiMode();
  if (gameApi === null || gameApiMode !== nextMode) {
    gameApi = createGameApi("celestia", nextMode);
    gameApiMode = nextMode;
  }
  return gameApi;
}

// ---------------------------------------------------------------------------
// State shape — `viewerState` is a ViewerState (projected), never GameState.
// ---------------------------------------------------------------------------

export interface CelestiaMultiplayerState {
  /** Server-projected view for THIS user. Never a raw GameState. */
  readonly viewerState: ViewerState | null;
  readonly lastError: { readonly error: string } | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
}

type ConnectionStatus = CelestiaMultiplayerState["connection"];

const INITIAL_STATE: CelestiaMultiplayerState = {
  viewerState: null,
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  myPlayerId: null,
  isHost: false,
};

let state: CelestiaMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

interface ViewerStateRow {
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
}

/**
 * Adopt the highest-seq viewer-state row from a fetched batch. Returns the
 * applied row's seq, or the current seq when nothing newer arrived.
 */
function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }
  if (best !== null) {
    state = {
      ...state,
      viewerState: best as unknown as ViewerState,
      lastAppliedSeq: bestSeq,
    };
  }
  return bestSeq;
}

export const celestiaMultiplayerActions = {
  /**
   * Host mount — server-secret seed. The host NEVER calls `initGame` and
   * NEVER holds a `GameState` or the RNG seed. On canonical Celestia routes it
   * submits a single rule-service `init` command. Legacy Celestia routes
   * keep the older bootstrap descriptor + `BootstrapMaterialize` handshake
   * until the shared catalog fully points at the v2 rule service.
   *
   * Bug class prevented: "host reload destroys in-progress game" — we never
   * re-init; we fetch viewer-states first and replay the newest. Bootstrap
   * is exactly-once via the validator idempotency cache.
   */
  initializeAsHost: async (
    roomId: string,
    playerIds: readonly string[],
    playerNames: readonly string[] = [],
  ): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: true,
      connection: "connecting",
    };
    notify();

    // Reconnect path: adopt any existing projected state for this user.
    const api = getGameApi();
    const mode = getGameApiMode();
    const existing = await api.getViewerStates(roomId);
    const existingRows = existing.success && existing.data ? existing.data : [];
    if (existingRows.length > 0) {
      adoptLatest(existingRows, 0);
      state = { ...state, connection: "open" };
      notify();
      return;
    }

    // Bootstrap — server-secret seed (ADR-04 v2). The host submits ONLY
    // public config (the bootstrap descriptor: NO rng, NO deck, NO hands,
    // NO version). The seed is generated by the validator via a CSPRNG
    // (`crypto.randomUUID()`), never derived from the public roomId, and
    // NEVER leaves the server. The store stays `ViewerState | null` — it
    // never holds a GameState and never knows the seed.
    const seating = Array.from(playerIds).map((id, i) => ({
      playerId: id,
      displayName: playerNames[i]?.trim() || id || `Player ${i + 1}`,
    }));
    const hostPlayerId = playerIds[0] ?? myPlayerId;
    const bootstrapActionId = `${roomId}:bootstrap`;

    const descriptor: Record<string, unknown> = {
      __bootstrap: true,
      gameId: roomId,
      playerSeating: seating,
      hostPlayerId,
      createdAt: new Date().toISOString(),
    };

    try {
      if (mode === "game-platform") {
        await api.submitEvent(roomId, "init", {
          actionId: `${roomId}:server-init`,
          playerSeating: seating,
        });
      } else {
        // 1. Store the public descriptor as room.CurrentState (frozen Go path).
        await api.submitEvent(roomId, "init", { state: descriptor });
        // 2. Trigger server-side materialization (reaches the validator —
        //    non-init event with CurrentState set). Idempotent on
        //    `${roomId}:bootstrap`, so a host double-mount generates the
        //    secret seed exactly once.
        await api.submitEvent(roomId, "BootstrapMaterialize", {
          actionId: bootstrapActionId,
        });
      }
    } catch (error) {
      state = {
        ...state,
        lastError: {
          error: error instanceof Error ? error.message : "ERR_INIT_FAILED",
        },
        connection: "closed",
      };
      notify();
      return;
    }

    // 3. Adopt the host's OWN server-projected ViewerState (the seed and
    //    every foreign hand are already stripped server-side). The store
    //    NEVER reconstructs a GameState.
    try {
      const projected = await api.getViewerStates(roomId);
      if (projected.success && projected.data && projected.data.length > 0) {
        adoptLatest(projected.data, 0);
      }
    } catch {
      // Non-fatal — the WS push / next replay delivers the projection.
    }
    state = { ...state, connection: "open" };
    notify();
  },

  /**
   * Guest mount. Guests NEVER init; they fetch their own projected
   * viewer-states and adopt the latest.
   */
  initializeAsGuest: async (roomId: string): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      connection: "connecting",
    };
    notify();

    try {
      const res = await getGameApi().getViewerStates(roomId);
      if (res.success && res.data && state.roomId === roomId) {
        adoptLatest(res.data, 0);
      }
    } catch {
      // Non-fatal — WS reconnect / next push will deliver the projection.
    }
    state = { ...state, connection: "open" };
    notify();
  },

  /**
   * Re-fetch own viewer-states and adopt the newest. Called on WS reconnect
   * to catch any projection missed while disconnected.
   */
  replayEvents: async (roomId: string): Promise<void> => {
    try {
      const res = await getGameApi().getViewerStates(
        roomId,
        state.lastAppliedSeq,
      );
      if (res.success && res.data) {
        adoptLatest(res.data, state.lastAppliedSeq);
        notify();
      }
    } catch (error) {
      state = {
        ...state,
        lastError: {
          error:
            error instanceof Error ? error.message : "ERR_STATE_FETCH_FAILED",
        },
      };
      notify();
    }
  },

  /**
   * Apply a per-user WS `game:viewer_state` push. Deduplicates via
   * lastAppliedSeq (WS echo / replay overlap). The payload is ALREADY a
   * server-projected ViewerState for this user — rendered verbatim, never
   * re-derived from a GameState.
   */
  applyRemoteEvent: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    state = {
      ...state,
      viewerState: payloadState as unknown as ViewerState,
      lastAppliedSeq: seq,
    };
    notify();
  },

  /**
   * Submit an action. Sends `{ type, actionId, params }` ONLY — NO
   * optimistic apply (ADR-04: the client cannot legally compute the result).
   * The store waits for the server-projected ViewerState (WS push or the
   * post-submit fetch) to render the new state.
   */
  submitAction: async (
    action: Action,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!state.roomId) return { success: false, error: "ERR_NO_GAME" };
    const roomId = state.roomId;

    // The server fills rng/cardId itself (spec §6.3, §10). We forward
    // exactly the action's own params (type goes on the eventType slot).
    const { type, ...rest } = action as unknown as Record<string, unknown> & {
      type: string;
    };
    const payload: Record<string, unknown> = { ...rest };

    try {
      const response = await getGameApi().submitEvent(roomId, type, payload);
      if (!response.success) {
        const errMsg = response.error ?? "ERR_SUBMIT_FAILED";
        state = { ...state, lastError: { error: errMsg } };
        notify();
        return { success: false, error: errMsg };
      }
      // No optimistic apply: the projected ViewerState arrives via WS
      // (game:viewer_state). Pull once as a fallback in case the push raced.
      await celestiaMultiplayerActions.replayEvents(roomId);
      state = { ...state, lastError: null };
      notify();
      return { success: true };
    } catch (error) {
      const errMsg =
        error instanceof Error ? error.message : "ERR_SUBMIT_FAILED";
      state = { ...state, lastError: { error: errMsg } };
      notify();
      return { success: false, error: errMsg };
    }
  },

  setConnection: (status: ConnectionStatus): void => {
    state = { ...state, connection: status };
    notify();
  },

  clearError: (): void => {
    state = { ...state, lastError: null };
    notify();
  },

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

export const celestiaMultiplayerStore = {
  getSnapshot: (): CelestiaMultiplayerState => state,
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export function useCelestiaMultiplayerStore(): CelestiaMultiplayerState {
  return useSyncExternalStore(
    celestiaMultiplayerStore.subscribe,
    celestiaMultiplayerStore.getSnapshot,
  );
}
